import { MapPin, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo.png?w=154&format=webp&quality=85";
import { useSiteContent } from "@/hooks/useSiteContent";

const Footer = () => {
  const { data: siteContent } = useSiteContent();
  const { data: cmsPages } = useQuery({
    queryKey: ["cms-footer-pages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_pages")
        .select("title, slug")
        .eq("is_published", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
  const general = siteContent?.general;
  const footerCms = siteContent?.footer;
  const navData = siteContent?.nav_items;

  const brandDesc = footerCms?.brand_description || "Schule Nr. 1 nach Wahl der Eltern für optimale intellektuelle Entwicklung von Kindern von 3 bis 12 Jahren.";
  const copyright = (footerCms?.copyright || "© {year} Kleines Genie Österreich. Alle Rechte vorbehalten.").replace("{year}", new Date().getFullYear().toString());

  const programItems = navData?.program_items || [
    { label: "Numicon", href: "/programi/numicon" },
    { label: "Mentale Arithmetik", href: "/programi/mentalna-aritmetika" },
    { label: "Kleiner Alleswisser", href: "/programi/kleiner-alleswisser" },
    { label: "Programmieren für Kinder", href: "/programi/programmieren" },
  ];

  return (
    <footer className="bg-foreground text-primary-foreground/80 pt-10 md:pt-16 pb-6 md:pb-8">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7 md:gap-8 mb-8 md:mb-10">
          <div>
            <img src={logo} alt="Kleines Genie Logo" className="h-14 w-auto mb-4" width={154} height={84} />
            <p className="font-body text-sm leading-relaxed mb-4">{brandDesc}</p>
          </div>

          <div>
            <h4 className="font-heading font-800 text-primary-foreground mb-3 md:mb-4">Programme</h4>
            <ul className="space-y-2 font-body text-sm">
              {programItems.map((p: any) => (
                <li key={p.label}><a href={p.href} className="hover:text-primary-foreground transition-colors">{p.label}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-800 text-primary-foreground mb-3 md:mb-4">Schnelllinks</h4>
            <ul className="space-y-2 font-body text-sm">
              <li><Link to="/" className="hover:text-primary-foreground transition-colors">Startseite</Link></li>
              <li><a href="/#programi" className="hover:text-primary-foreground transition-colors">Programme</a></li>
              <li><a href="/#zasto-mi" className="hover:text-primary-foreground transition-colors">Warum wir</a></li>
              <li><a href="/#rezultati" className="hover:text-primary-foreground transition-colors">Ergebnisse</a></li>
              <li><a href="/#iskustva" className="hover:text-primary-foreground transition-colors">Erfahrungen</a></li>
              <li><Link to="/istrazivanja" className="hover:text-primary-foreground transition-colors">Forschung</Link></li>
              <li><a href="/#kontakt" className="hover:text-primary-foreground transition-colors">Kontakt</a></li>
              {(cmsPages || []).map((p: any) => (
                <li key={p.slug}><Link to={`/stranica/${p.slug}`} className="hover:text-primary-foreground transition-colors">{p.title}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-800 text-primary-foreground mb-3 md:mb-4">Standorte</h4>
            <div className="space-y-4 font-body text-sm">
              <div>
                <p className="font-bold text-primary-foreground mb-1">Wien 1220</p>
                <div className="flex items-center gap-2"><MapPin className="w-3 h-3 flex-shrink-0" /><span>Großenzersdorfer Str. 56/1</span></div>
                <div className="flex items-center gap-2"><Phone className="w-3 h-3 flex-shrink-0" /><a href="tel:+436509861983" className="hover:text-primary-foreground transition-colors">+43 650 9861983</a></div>
                <div className="flex items-center gap-2"><Mail className="w-3 h-3 flex-shrink-0" /><a href="mailto:info@kleines-genie.at" className="hover:text-primary-foreground transition-colors">info@kleines-genie.at</a></div>
              </div>
              <div>
                <p className="font-bold text-primary-foreground mb-1">Schwechat</p>
                <div className="flex items-center gap-2"><MapPin className="w-3 h-3 flex-shrink-0" /><span>Ableidingergasse 4/24</span></div>
                <div className="flex items-center gap-2"><Phone className="w-3 h-3 flex-shrink-0" /><a href="tel:+4369010456793" className="hover:text-primary-foreground transition-colors">+43 690 10456793</a></div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-primary-foreground/10 pt-6 md:pt-8 text-center">
          <p className="font-body text-sm">{copyright}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
